import Image from 'components/Basic/Image/Image';
import { LazyGoogleMap, LazyPigeonMap, LazySeznamMap } from 'components/Basic/Map/LazyMaps';
import { showInfoMessage } from 'components/Helpers/Toasts';
import { StoreLocatorControls } from 'components/Pages/Stores/StoreLocatorControls';
import { MAP_SELECTED_STORE_ZOOM, MINIMAL_SEARCH_QUERY_LENGTH } from 'config/constants';
import { StoreTerritorialUnitType } from 'connectors/stores/StoreLocator';
import { useMapContext } from 'context/Map/useMapContext';
import useTranslation from 'next-translate/useTranslation';
import { ComponentType, useCallback, useEffect, useMemo } from 'react';
import { MapMarkerType, MapProps, MapProviderTypeEnum } from 'types/map';
import { StoreLocatorStoreType, StoreServiceFiltersType, StoreServiceIconsType } from 'types/store';

type StoreLocatorMapProps = {
    isLoading: boolean;
    geolocationRequestId: number;
    geolocatedStoreUuid: string | null;
    mapProvider: string | undefined;
    searchValue: string;
    selectedRegion: string;
    selectedDistrict: string;
    territorialUnits: StoreTerritorialUnitType[];
    areTerritorialUnitsLoading: boolean;
    availableServices: StoreServiceFiltersType;
    serviceFilters: StoreServiceFiltersType;
    serviceIcons: StoreServiceIconsType;
    stores: StoreLocatorStoreType[];
    onSearchChange: (value: string) => void;
    onGeolocate: (latitude: number, longitude: number) => void;
    onRegionChange: (value: string) => void;
    onDistrictChange: (value: string) => void;
    onTerritorialSelectorFocus: () => void;
    onServiceFilterChange: (service: keyof StoreServiceFiltersType, isChecked: boolean) => void;
};

const getMapComponent = (provider: string | undefined): ComponentType<MapProps> | null => {
    if (provider === MapProviderTypeEnum.OSM) {
        return LazyPigeonMap;
    }
    if (provider === MapProviderTypeEnum.SEZNAM) {
        return LazySeznamMap;
    }
    if (provider === MapProviderTypeEnum.GOOGLE) {
        return LazyGoogleMap;
    }

    return null;
};

export const StoreLocatorMap: FC<StoreLocatorMapProps> = ({
    isLoading,
    geolocationRequestId,
    geolocatedStoreUuid,
    mapProvider,
    searchValue,
    selectedRegion,
    selectedDistrict,
    territorialUnits,
    areTerritorialUnitsLoading,
    availableServices,
    serviceFilters,
    serviceIcons,
    stores,
    onSearchChange,
    onGeolocate,
    onRegionChange,
    onDistrictChange,
    onTerritorialSelectorFocus,
    onServiceFilterChange,
}) => {
    const { t } = useTranslation();
    const [, dispatch] = useMapContext();

    const clickOnMarkerHandler = useCallback(
        (uuid: string) => {
            const store = stores.find((store) => store.uuid === uuid);

            if (store?.locationLatitude === null || store?.locationLongitude === null || store === undefined) {
                return;
            }

            dispatch({
                type: 'setCenter',
                payload: { lng: store.locationLongitude, lat: store.locationLatitude },
            });
            dispatch({ type: 'setZoom', payload: MAP_SELECTED_STORE_ZOOM });
            dispatch({ type: 'setActiveMarkerUuid', payload: uuid });
            dispatch({ type: 'setIsFitToBounds', payload: false });
        },
        [dispatch, stores],
    );

    const markers = useMemo<MapMarkerType[]>(
        () =>
            stores.flatMap((store) => {
                if (store.locationLatitude === null || store.locationLongitude === null) {
                    return [];
                }

                return [
                    {
                        identifier: store.uuid,
                        lat: store.locationLatitude,
                        lng: store.locationLongitude,
                        storeName: store.name,
                        storeStreet: store.street,
                        storeCity: store.city,
                        storeSlug: store.slug,
                        isPickupPoint: store.isPickupPoint,
                    },
                ];
            }),
        [stores],
    );

    const getUserGeolocationHandler = useCallback(() => {
        if (!('geolocation' in navigator)) {
            showInfoMessage(t('Location access is unavailable. Please enable it in your browser settings.'));

            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => onGeolocate(position.coords.latitude, position.coords.longitude),
            () => showInfoMessage(t('Location access is unavailable. Please enable it in your browser settings.')),
        );
    }, [onGeolocate, t]);

    const MapComponent = getMapComponent(mapProvider);
    const shouldShowNotFoundMessage =
        !isLoading &&
        (searchValue.trim().length >= MINIMAL_SEARCH_QUERY_LENGTH ||
            selectedRegion !== '' ||
            selectedDistrict !== '' ||
            Object.values(serviceFilters).some(Boolean)) &&
        stores.length === 0;

    useEffect(() => {
        dispatch({ type: 'setActiveMarkerUuid', payload: undefined });

        const geolocatedStore = stores.find(({ uuid }) => uuid === geolocatedStoreUuid);

        if (
            geolocatedStore !== undefined &&
            geolocatedStore.locationLatitude !== null &&
            geolocatedStore.locationLongitude !== null
        ) {
            dispatch({
                type: 'setCenter',
                payload: { lng: geolocatedStore.locationLongitude, lat: geolocatedStore.locationLatitude },
            });
            dispatch({ type: 'setZoom', payload: MAP_SELECTED_STORE_ZOOM });
            dispatch({ type: 'setIsFitToBounds', payload: false });

            return;
        }

        dispatch({ type: 'setIsFitToBounds', payload: true });
    }, [dispatch, geolocatedStoreUuid, geolocationRequestId, stores]);

    return (
        <>
            <StoreLocatorControls
                areTerritorialUnitsLoading={areTerritorialUnitsLoading}
                availableServices={availableServices}
                searchValue={searchValue}
                selectedDistrict={selectedDistrict}
                selectedRegion={selectedRegion}
                serviceFilters={serviceFilters}
                serviceIcons={serviceIcons}
                territorialUnits={territorialUnits}
                onDistrictChange={onDistrictChange}
                onGeolocation={getUserGeolocationHandler}
                onRegionChange={onRegionChange}
                onSearchChange={onSearchChange}
                onServiceFilterChange={onServiceFilterChange}
                onTerritorialSelectorFocus={onTerritorialSelectorFocus}
            />
            <div className="relative mb-5 h-[255px] w-full md:mb-[60px] md:h-[420px]">
                {MapComponent !== null && (
                    <MapComponent
                        clickOnMarkerHandler={clickOnMarkerHandler}
                        markers={markers}
                        shouldShowSpinner={isLoading}
                    />
                )}
                {shouldShowNotFoundMessage && (
                    <div className="absolute left-1/2 top-1/2 z-two flex min-w-[240px] -translate-x-1/2 -translate-y-1/2 items-center rounded-md bg-white/90 px-5 py-[15px]">
                        <Image alt={t('warning')} height={24} src="/icons/warning.png" width={24} />
                        <span className="flex-1 pl-4 text-small">
                            {t('Could not find any results for the given query.')}
                        </span>
                    </div>
                )}
            </div>
        </>
    );
};
